import { useEffect, useRef } from 'react';
import { getRumInstance } from 'src/rum';
import { useCurrentUser } from '../auth';

/**
 * Event name shared with legacy (src/App.tsx records the same event) so
 * existing RUM dashboard widgets pick up v2 sessions without new queries.
 */
const USER_ACCESS_EVENT = 'user_access';

/**
 * Attaches user context to the RUM session and records the `user_access`
 * event, mirroring what the legacy app shell does in src/App.tsx.
 *
 * Session attributes (userId, programId, businessUnit) let dashboards
 * slice v2 sessions the same way as v1 sessions. The access event fires
 * once per mount, and only after a business unit is known — matching the
 * legacy emission shape exactly for dashboard continuity.
 *
 * Must be mounted inside <UserProvider> (reads useCurrentUser).
 */
export function useRumSessionContext(): void {
  const { alias, programId, businessUnit } = useCurrentUser();
  const hasRecordedAccess = useRef(false);

  useEffect(() => {
    void getRumInstance().then((rum) => {
      if (!rum) return;
      rum.addSessionAttributes({
        userId: alias,
        programId,
        ...(businessUnit ? { businessUnit } : {}),
      });
      if (businessUnit && !hasRecordedAccess.current) {
        hasRecordedAccess.current = true;
        rum.recordEvent(USER_ACCESS_EVENT, {
          userId: alias,
          programId,
          businessUnit,
          accessedAt: new Date().toISOString(),
        });
      }
    });
  }, [alias, programId, businessUnit]);
}
